/* The metadata Copilot, as the redesign lays it out: a conversation beside what Copilot can see.
 *
 * **One conversation, shared with the drawer.** The thread is the store in `model/thread`, so a
 * question asked from the top bar is here when the reader opens the full Copilot, and the other way
 * round. Empty, the page invites a question with suggestions in three groups; with a thread, each
 * turn is the question and its answer beside the Copilot's mark, and "New conversation" clears it.
 *
 * **Two rules from before the redesign, kept because they are the module's safety story.**
 *
 * *Status decides whether `text` is an answer.* Every outcome is a 201 carrying prose and only
 * `answered` is prose a model wrote and a validator accepted; the other four are host-written
 * fallback. `TurnView` presents each by its status, in the one component the drawer uses too.
 *
 * *Nothing on this page spends a completion by accident.* A suggestion fills the composer rather than
 * asking, and a recent question opens its stored answer rather than asking again — both differ from
 * the design's prototype, where either click asks, because asking is the one action here somebody
 * is charged for on a hosted endpoint.
 *
 * **The bounds are read from `/capabilities`**, never written into the page: the question cap, the
 * snippet cap, the retrievable kinds with the permission governing each, and what is never
 * retrieved. A deployment that changes one changes this screen.
 */

import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ageText } from '@/shared/model/age';
import { Alert } from '@/shared/ui/Alert';
import { CopilotMascot } from '@/shared/ui/CopilotMascot';
import { HelpLabel } from '@/shared/ui/InfoTip';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useCapabilities, useTranscript } from '../api/copilot';
import { useCopilotThread } from '../api/thread';
import { STATUS_LABEL, STATUS_TONE } from '../model/copilot';

import { TurnView } from './CopilotTurn';

/** The fallback question cap, used only until `/capabilities` answers.
 *
 * The deployment's own number is the authority — a client that hard-coded it would keep refusing at
 * 500 the day an operator raised the limit. This exists so the composer can judge length during the
 * first paint rather than against `undefined`.
 */
const FALLBACK_MAX_CHARS = 500;

/** How many transcript rows "Recent questions" lists. The transcript is the full record; this is a
 * way back into the last few, not a second history screen. */
const RECENT_SHOWN = 8;

/** The design's three groups of suggested questions.
 *
 * Static copy rather than an API read, and that is honest: there is no suggestions endpoint, and
 * these are examples of what the retrievable kinds can answer. Every one asks about a kind Copilot
 * may retrieve — a suggestion it could only answer with "no matching metadata" would teach a reader
 * that the feature does not work.
 */
const SUGGESTION_GROUPS = [
  {
    title: 'copilot.group.overview',
    keys: [
      'copilotDrawer.suggest.whyEstate',
      'copilotDrawer.suggest.attention',
      'copilotDrawer.suggest.incidents',
    ],
  },
  {
    title: 'copilot.group.datasets',
    keys: [
      'copilot.starter.profiled',
      'copilotDrawer.suggest.pii',
      'copilotDrawer.suggest.costliestRules',
    ],
  },
  {
    title: 'copilot.group.lineage',
    keys: ['copilotDrawer.suggest.downstream', 'copilot.suggest.lineageFrom'],
  },
] as const;

/** The query parameter another screen hands a question over in — the Overview's starters. */
const HANDED_OVER = 'q';

/** A deployment sentence saying no tool can change anything. */
const READ_ONLY = /^\s*(none|no tools)\b/iu;

/** A deployment sentence without the emphasis markers an engineer's prose carries. */
function plain(text: string): string {
  return text.replace(/\*\*/gu, '');
}

/** What Copilot can see, read from the deployment. */
function CanSee(): ReactElement {
  const { t } = useTranslation();
  const { data, isPending, isError } = useCapabilities();

  return (
    <section className="lq-copage__card" aria-labelledby="copilot-can-see">
      <h2 id="copilot-can-see" className="lq-copage__cardTitle">
        {t('copilot.canSee')}
      </h2>
      {isPending ? <SkeletonRows rows={4} /> : null}
      {/* A failed read is reported rather than rendered as an empty boundary: a card saying nothing
          is retrievable would be a claim about this deployment's permissions. */}
      {isError ? (
        <Alert tone="warning" title={t('copilot.boundaryFailed')}>
          {t('copilot.boundaryFailedBody')}
        </Alert>
      ) : null}
      {data === undefined ? null : (
        <>
          <dl className="lq-copage__kinds">
            {data.retrievable.map((entry) => (
              <div key={entry.kind} className="lq-copage__kind">
                <dt>{t(`copilot.cite.${entry.kind}`, { defaultValue: entry.kind })}</dt>
                <dd className="lq-copage__perm">{entry.governed_by}</dd>
              </div>
            ))}
          </dl>
          <p className="lq-copage__line">
            <strong>{t('copilot.neverRetrievedLabel')}</strong>{' '}
            {data.never_retrieved.join(' · ')}
          </p>
          {/* The deployment's sentences are written for an engineer — the tools one cites a module
              brief, the policy one runs to a paragraph — and printed whole they buried the card.
              The card says each in one line and keeps the deployment's own words on hover, so the
              granularity claim is still there in the platform's words for whoever checks it. */}
          <p className="lq-copage__line" title={plain(data.mutating_tools)}>
            {READ_ONLY.test(data.mutating_tools)
              ? t('copilot.readOnly')
              : plain(data.mutating_tools)}{' '}
            {t('copilot.snippetCap', { count: data.max_snippets })}
          </p>
          <p className="lq-copage__policy" title={plain(data.authorization_policy)}>
            {t('copilot.policyShort')}
          </p>
        </>
      )}
    </section>
  );
}

/** The last few questions this tenant asked, each opening its stored answer in the conversation. */
function Recent({ onOpen }: { readonly onOpen: (id: string) => void }): ReactElement {
  const { t } = useTranslation();
  const transcript = useTranscript();
  const [now] = useState(() => new Date());
  const rows = (transcript.data ?? []).slice(0, RECENT_SHOWN);

  return (
    <section className="lq-copage__card" aria-labelledby="copilot-recent">
      <h2 id="copilot-recent" className="lq-copage__cardTitle">
        {t('copilot.recent')}
      </h2>
      {transcript.isPending ? <SkeletonRows rows={3} /> : null}
      {/* **An absence is only claimed when it was actually read.** "Nothing asked yet" from a request
          that failed would be a claim about this tenant's history with no grounds. */}
      {transcript.isError ? (
        <p className="lq-copage__line">{t('copilot.historyFailedBody')}</p>
      ) : null}
      {transcript.isSuccess && rows.length === 0 ? (
        <p className="lq-copage__line">{t('copilot.noHistory')}</p>
      ) : null}
      {rows.length > 0 ? (
        <ul className="lq-copage__recent">
          {rows.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                className="lq-copage__recentRow"
                onClick={() => {
                  onOpen(row.id);
                }}
              >
                <span className="lq-copage__recentQ">{row.question}</span>
                <span className="lq-copage__recentMeta">
                  <span title={row.asked_at}>
                    {ageText(t, row.asked_at, now) ?? row.asked_at}
                  </span>
                  {' · '}
                  <span style={{ color: STATUS_TONE[row.status].color }}>
                    {STATUS_LABEL[row.status]}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export function CopilotConsole(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { turns, asking, send, show, clear } = useCopilotThread();
  const capabilities = useCapabilities();
  const transcript = useTranscript();
  const input = useRef<HTMLInputElement>(null);
  const last = useRef<HTMLDivElement>(null);

  /* **A handed-over question fills the composer and stops there.** Asking spends a completion, and
     only the reader may decide to; a link that asked would spend one per click on somebody's behalf.
     Read once as the initial value and then taken off the address, so a reload does not put back a
     question the reader has since cleared. */
  const [params, setParams] = useSearchParams();
  const [question, setQuestion] = useState(() => params.get(HANDED_OVER) ?? '');
  useEffect(() => {
    if (!params.has(HANDED_OVER)) return;
    const rest = new URLSearchParams(params);
    rest.delete(HANDED_OVER);
    setParams(rest, { replace: true });
  }, [params, setParams]);

  /* Bring the newest turn into view as the conversation grows. Guarded because jsdom has no
     `scrollIntoView`, and a missing method must not cost a reader their answer. */
  useEffect(() => {
    const anchor = last.current;
    if (anchor !== null && 'scrollIntoView' in anchor) {
      anchor.scrollIntoView({ block: 'nearest' });
    }
  }, [turns.length]);

  const maxChars = capabilities.data?.max_question_chars ?? FALLBACK_MAX_CHARS;
  const over = question.length > maxChars;
  const valid = question.trim() !== '' && !over;

  const submit = (): void => {
    if (!valid || asking) return;
    send(question);
    setQuestion('');
  };

  const fill = (text: string): void => {
    setQuestion(text);
    input.current?.focus();
  };

  return (
    <div className="lq-copage">
      <div className="lq-copage__main">
        {turns.length === 0 ? (
          <>
            {/* The design's invitation: the Copilot's own mark, what it answers from, and what it
                never sees — the first thing worth knowing about a model's answers. */}
            <div className="lq-copage__hero">
              <CopilotMascot state="idle" size="xl" />
              <h1 className="lq-copage__heroTitle">{t('copilot.noQuestions')}</h1>
              <p className="lq-copage__heroBody">{t('copilot.heroBody')}</p>
            </div>
            <div className="lq-copage__groups">
              {SUGGESTION_GROUPS.map((group) => (
                <section
                  key={group.title}
                  className="lq-copage__group"
                  aria-label={t(group.title)}
                >
                  <h2 className="lq-copage__groupTitle">{t(group.title)}</h2>
                  {group.keys.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className="lq-copage__suggestion"
                      onClick={() => {
                        fill(t(key));
                      }}
                    >
                      {t(key)}
                    </button>
                  ))}
                </section>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="lq-copage__threadHead">
              <HelpLabel
                subject={t('copilot.title')}
                help={t('help.feature.copilot')}
                className="lq-infotip-row lq-infotip-row--grow"
              >
                <h1 className="lq-copage__title">{t('copilot.title')}</h1>
              </HelpLabel>
              <button type="button" className="lq-copage__new" onClick={clear}>
                {t('copilot.newConversation')}
              </button>
            </div>
            <ol className="lq-cop__thread lq-copage__thread" aria-live="polite">
              {turns.map((turn) => (
                <TurnView key={turn.id} turn={turn} onNavigate={navigate} withMascot />
              ))}
            </ol>
            <div ref={last} />
          </>
        )}

        <form
          className="lq-copage__composer"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <div className="lq-copage__composerRow">
            <input
              ref={input}
              className="lq-copage__input"
              type="text"
              value={question}
              aria-label={t('copilot.questionLabel')}
              placeholder={t('copilot.placeholder')}
              onChange={(event) => {
                setQuestion(event.target.value);
              }}
            />
            <button type="submit" className="lq-copage__send" disabled={!valid || asking}>
              {asking ? t('copilot.asking') : t('copilot.ask')}
            </button>
          </div>
          {/* **Refused whole rather than trimmed**, which is what the server does — so the screen
              says so instead of silently cutting the question the person wrote. */}
          {over ? (
            <p className="lq-copage__over">
              {t('copilot.overBy', { over: question.length - maxChars })}
            </p>
          ) : null}
        </form>
      </div>

      <aside className="lq-copage__side">
        <CanSee />
        <Recent
          onOpen={(id) => {
            const stored = transcript.data?.find((row) => row.id === id);
            if (stored !== undefined) show(stored);
          }}
        />
      </aside>
    </div>
  );
}
